import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  Tabs,
  TabList,
  TabPanels,
  TabPanel,
  Tab,
  Spinner,
  Center,
  Alert,
  AlertIcon,
  VStack,
  HStack,
  Box,
  SimpleGrid,
  Text,
  Badge,
  Code,
  Button,
  List,
  ListItem,
  ListIcon,
  FormControl,
  FormLabel,
  FormHelperText,
  FormErrorMessage,
  Input,
  Textarea,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  useColorModeValue,
  AlertDialog,
  AlertDialogOverlay,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogBody,
  AlertDialogFooter,
} from '@chakra-ui/react'
import { CheckCircleIcon, WarningIcon } from '@chakra-ui/icons'
import { useMemo, useState, useEffect, useRef, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'
import type {
  OrderListItem,
  OrderLifecycleResponse,
  OrderLifecycleRentRecoveryRecord,
  OrderLifecycleSweepExtend,
  OrderLifecycleOnchainCostItem,
  EvmOperationEnvelope,
  EvmPendingOperationItem,
} from '@/types'
import type { NetworkEnvironment } from '@/hooks/useNetworkEnvironment'
import { formatDateTime } from '@/utils/formatUtils'
import { useToast } from '@/hooks/useToast'
import { orderService } from '@/services/admin/orderService'
import { settlementService } from '@/services/admin/settlementService'
import { RefreshIconButton } from '@/components/common/RefreshIconButton'
import { OrderLifecycleTimeline } from './OrderLifecycleTimeline'
import type { GovernanceAction } from './orderLifecycleTypes'
import { CopyIconButton, ExplorerButton } from './orderLifecycleShared'
import { OrderGovernanceSection } from './OrderGovernanceSection'
import { OrderRefundsSection } from './OrderRefundsSection'
import { OrderPaymentsSection } from './OrderPaymentsSection'
import { OrderSettlementsSection } from './OrderSettlementsSection'
import { OrderDeploymentsSection } from './OrderDeploymentsSection'
import { OrderRentRecoverySection } from './OrderRentRecoverySection'
import { OrderLifecycleDocumentsTab } from './OrderLifecycleDocumentsTab'
import { OrderLifecycleDocPolicyTab } from './OrderLifecycleDocPolicyTab'
import { OrderStateConsistencyTab } from './OrderStateConsistencyTab'
import { useSolanaOrderGovernanceState } from '@/hooks/useOrderConsistencyData'
import { useAuth } from '@/contexts/AuthContext'
import { evmOperationService } from '@/services/admin/evmOperationService'

interface OrderLifecycleModalProps {
  isOpen: boolean
  onClose: () => void
  order?: OrderListItem | null
  lifecycle?: OrderLifecycleResponse
  isLoading?: boolean
  onRefresh?: () => void
  mode: NetworkEnvironment
}

const formatStatus = (status: string | undefined, translate: TFunction) => {
  if (!status) {
    return translate('common.na')
  }
  const normalized = status.toLowerCase()
  const translationKey = `orders.status.${normalized}`
  const result = translate(translationKey)
  return result === translationKey ? normalized.replace(/_/g, ' ') : result
}

const truncateMiddle = (value?: string | null, length = 10) => {
  if (!value) return ''
  if (value.length <= length * 2) return value
  return `${value.slice(0, length)}...${value.slice(-length)}`
}

const formatEvmOperationError = (error: unknown): string => {
  if (error instanceof Error && error.message.trim()) {
    return error.message
  }
  return 'Unable to recover the EVM execution operation'
}

const EVM_TERMINAL_STATES = new Set(['completed', 'rejected', 'failed'])

export const normalizeAmountFromSmallest = (
  smallest?: string | null,
  decimals?: number | null
): string | undefined => {
  if (!smallest || smallest.trim().length === 0) {
    return undefined
  }
  const trimmed = smallest.trim()
  if (!/^-?\d+$/u.test(trimmed)) {
    return trimmed
  }

  const decimalsSafe =
    typeof decimals === 'number' && Number.isFinite(decimals) && decimals >= 0
      ? Math.trunc(decimals)
      : null
  if (decimalsSafe === null) {
    return undefined
  }
  if (decimalsSafe === 0) {
    return trimmed
  }

  const isNegative = trimmed.startsWith('-')
  const unsigned = isNegative ? trimmed.slice(1) : trimmed

  const padded = unsigned.padStart(decimalsSafe + 1, '0')
  const integerPartRaw = padded.slice(0, -decimalsSafe) || '0'
  const fractionRaw = padded.slice(-decimalsSafe)
  const integerPart = integerPartRaw.replace(/^0+(?=\d)/u, '') || '0'
  const fraction = fractionRaw.replace(/0+$/u, '')
  const combined = fraction.length > 0 ? `${integerPart}.${fraction}` : integerPart
  return isNegative ? `-${combined}` : combined
}

const formatAmount = (value?: string | null) => {
  if (!value) {
    return 'N/A'
  }
  const numeric = Number(value)
  if (Number.isNaN(numeric)) {
    return value
  }
  return numeric.toString()
}

const formatTimestamp = (value?: string | null, locale?: string) => {
  if (!value) {
    return null
  }
  return formatDateTime(value, {
    locale: locale ?? (typeof window !== 'undefined' ? navigator.language : 'en-US'),
  })
}

const renderKeyValue = (label: string, value?: string | null) => {
  if (!value) {
    return null
  }
  return (
    <HStack spacing={3} align="flex-start">
      <Text fontSize="sm" fontWeight="medium" minW="140px">
        {label}
      </Text>
      <Text fontSize="sm" flex="1">
        {value}
      </Text>
    </HStack>
  )
}

const EVM_TIMELINE_LABEL_MAP: Record<string, string> = {
  order_created: 'orders.lifecycle.timelineLabels.order_created',
  order_closed: 'orders.lifecycle.timelineLabels.order_closed',
  payment_confirmed: 'orders.lifecycle.timelineLabels.payment_confirmed',
  contract_preparation_pending: 'orders.lifecycle.timelineLabels.contract_preparation_pending',
  contract_registry_ready: 'orders.lifecycle.timelineLabels.contract_registry_ready',
  contract_deployed: 'orders.lifecycle.timelineLabels.contract_deployed',
  contract_initialized: 'orders.lifecycle.timelineLabels.contract_initialized',
  contract_verified: 'orders.lifecycle.timelineLabels.contract_verified',
  settlement_verified: 'orders.lifecycle.timelineLabels.settlement_verified',
  refund_completed: 'orders.lifecycle.timelineLabels.refund_completed',
  contract_paused: 'orders.lifecycle.timelineLabels.contract_paused',
  contract_resumed: 'orders.lifecycle.timelineLabels.contract_resumed',
  contract_closed: 'orders.lifecycle.timelineLabels.contract_closed',
}

const SOLANA_TIMELINE_LABEL_MAP: Record<string, string> = {
  ...EVM_TIMELINE_LABEL_MAP,
  contract_deployed: 'orders.lifecycle.timelineLabels.solana_initialization_submitted',
  contract_initialized: 'orders.lifecycle.timelineLabels.solana_initialized',
  contract_verified: 'orders.lifecycle.timelineLabels.solana_verified',
}

export function OrderLifecycleModal({
  isOpen,
  onClose,
  order,
  lifecycle,
  isLoading,
  onRefresh,
  mode,
}: OrderLifecycleModalProps) {
  const { t } = useTranslation()
  const { user } = useAuth()
  const locale = typeof window !== 'undefined' ? navigator.language : 'en-US'
  const { success: showSuccessToast, error: showErrorToast, warning: showWarningToast } = useToast()
  const [newUnlockAt, setNewUnlockAt] = useState('')
  const [activeTabIndex, setActiveTabIndex] = useState(0)
  const [documentTabsRefreshToken, setDocumentTabsRefreshToken] = useState(0)
  const [isDocumentsLoading, setIsDocumentsLoading] = useState(false)
  const [isDocPolicyLoading, setIsDocPolicyLoading] = useState(false)
  const [extendNotes, setExtendNotes] = useState('')
  const [isExtending, setIsExtending] = useState(false)
  const [durationOffsets, setDurationOffsets] = useState({
    years: 0,
    months: 0,
    days: 0,
    hours: 0,
  })
  const [governanceNotes, setGovernanceNotes] = useState('')
  const [activeGovernanceAction, setActiveGovernanceAction] = useState<GovernanceAction | null>(null)
  const [pendingGovernanceAction, setPendingGovernanceAction] = useState<GovernanceAction | null>(null)
  const [isTriggeringManualCleanup, setIsTriggeringManualCleanup] = useState(false)
  const [verifyingCleanupRecordId, setVerifyingCleanupRecordId] = useState<string | null>(null)
  const [retryingCleanupRecordId, setRetryingCleanupRecordId] = useState<string | null>(null)
  const [pendingEvmGovernance, setPendingEvmGovernance] =
    useState<EvmPendingOperationItem | null>(null)
  const [evmGovernanceOperation, setEvmGovernanceOperation] =
    useState<EvmOperationEnvelope | null>(null)
  const [evmGovernanceError, setEvmGovernanceError] = useState<string | null>(null)
  const [hasQuarantinedEvmGovernance, setHasQuarantinedEvmGovernance] = useState(false)
  const [isRefreshingEvmGovernance, setIsRefreshingEvmGovernance] = useState(false)
  const cancelGovernanceRef = useRef<HTMLButtonElement | null>(null)
  const onchainCosts = lifecycle?.onchainCosts?.items ?? []
  const selectFirstValue = (...values: Array<string | number | null | undefined>) => {
    for (const candidate of values) {
      if (candidate === undefined || candidate === null) {
        continue
      }
      const text = String(candidate).trim()
      if (text.length > 0) {
        return candidate
      }
    }
    return undefined
  }

  useEffect(() => {
    if (!isOpen) {
      setNewUnlockAt('')
      setActiveTabIndex(0)
      setExtendNotes('')
      setIsExtending(false)
      setDurationOffsets({
        years: 0,
        months: 0,
        days: 0,
        hours: 0,
      })
      setGovernanceNotes('')
      setActiveGovernanceAction(null)
      setPendingGovernanceAction(null)
      setIsTriggeringManualCleanup(false)
      setVerifyingCleanupRecordId(null)
      setRetryingCleanupRecordId(null)
      setPendingEvmGovernance(null)
      setEvmGovernanceOperation(null)
      setEvmGovernanceError(null)
      setHasQuarantinedEvmGovernance(false)
    }
  }, [isOpen])

  const toLocalInputValue = (date: Date | null | undefined) => {
    if (!date || Number.isNaN(date.getTime())) {
      return ''
    }
    const offset = date.getTimezoneOffset() * 60000
    return new Date(date.getTime() - offset).toISOString().slice(0, 16)
  }

  const handleDurationChange = (field: 'years' | 'months' | 'days' | 'hours', value: number) => {
    setDurationOffsets(previous => ({
      ...previous,
      [field]: Number.isFinite(value) && value >= 0 ? Math.trunc(value) : 0,
    }))
  }

  const applyDurationOffsets = (base: Date | null | undefined) => {
    const baseDate = base && !Number.isNaN(base.getTime()) ? new Date(base) : new Date()
    const result = new Date(baseDate)
    if (durationOffsets.years) {
      result.setFullYear(result.getFullYear() + durationOffsets.years)
    }
    if (durationOffsets.months) {
      result.setMonth(result.getMonth() + durationOffsets.months)
    }
    if (durationOffsets.days) {
      result.setDate(result.getDate() + durationOffsets.days)
    }
    if (durationOffsets.hours) {
      result.setHours(result.getHours() + durationOffsets.hours)
    }
    setNewUnlockAt(toLocalInputValue(result))
  }

  const explorerChainId = selectFirstValue(
    lifecycle?.order?.chain?.numeric,
    lifecycle?.order?.chain?.id,
    order?.chainId,
    lifecycle?.order?.chain?.code,
    order?.chainLabel
  )

  const displayChainLabel = selectFirstValue(
    lifecycle?.order?.chain?.code,
    lifecycle?.order?.chain?.id,
    order?.chainLabel,
    order?.chainId,
    lifecycle?.order?.chain?.numeric
  )
  const chainCompatibility =
    lifecycle?.order?.chain?.compatibility ?? order?.chainCompatibility ?? null
  const isSolana = chainCompatibility?.trim().toLowerCase() === 'solana'
  const isEvm = chainCompatibility?.trim().toLowerCase() === 'evm'
  const lifecycleOrderId = lifecycle?.order?.id ?? order?.orderId ?? null
  const successfulEvmDeployment = useMemo(() => {
    if (!isEvm) {
      return null
    }
    return [...(lifecycle?.deployments ?? [])]
      .filter(deployment => (deployment.status ?? '').toLowerCase() === 'successful')
      .sort((left, right) => right.attempt - left.attempt)[0] ?? null
  }, [isEvm, lifecycle?.deployments])
  const hasNonterminalEvmGovernance = isEvm
    && evmGovernanceOperation !== null
    && !EVM_TERMINAL_STATES.has(evmGovernanceOperation.state)
  const solanaGovernanceStateQuery = useSolanaOrderGovernanceState(lifecycleOrderId, {
    enabled:
      isOpen
      && isSolana
      && order?.solanaOrderPauseSupported === true,
  })
  const refetchSolanaGovernanceState = solanaGovernanceStateQuery.refetch
  const refreshGovernanceState = useCallback(() => {
    onRefresh?.()
    if (isSolana && order?.solanaOrderPauseSupported === true) {
      void refetchSolanaGovernanceState()
    }
  }, [isSolana, onRefresh, order?.solanaOrderPauseSupported, refetchSolanaGovernanceState])

  const handleUnifiedRefresh = useCallback(() => {
    setDocumentTabsRefreshToken(current => current + 1)
    refreshGovernanceState()
  }, [refreshGovernanceState])

  const syncPendingEvmGovernance = useCallback((idempotencyKey: string) => {
    if (!user?.id || !mode || !successfulEvmDeployment?.id) {
      return
    }
    const hydrated = evmOperationService.hydratePending(user.id, mode)
    const resourceItems = hydrated.items.filter(item =>
      item.resourceType === 'governance_deployment'
      && item.resourceId === successfulEvmDeployment.id
    )
    setPendingEvmGovernance(
      resourceItems.find(item => item.idempotencyKey === idempotencyKey)
      ?? resourceItems.at(-1)
      ?? null
    )
    if (hydrated.quarantinedKeys.length > 0) {
      setHasQuarantinedEvmGovernance(true)
    }
  }, [mode, successfulEvmDeployment?.id, user?.id])

  const reconcileEvmGovernance = useCallback(async (item: EvmPendingOperationItem) => {
    if (!user?.id || !mode) {
      throw new Error('Admin identity or network mode is unavailable')
    }
    setIsRefreshingEvmGovernance(true)
    try {
      const operation = await evmOperationService.reconcilePending(user.id, mode, item)
      setEvmGovernanceOperation(operation)
      setEvmGovernanceError(null)
      syncPendingEvmGovernance(item.idempotencyKey)
      return operation
    } catch (error) {
      setEvmGovernanceError(formatEvmOperationError(error))
      throw error
    } finally {
      setIsRefreshingEvmGovernance(false)
    }
  }, [mode, syncPendingEvmGovernance, user?.id])

  const loadLatestEvmGovernance = useCallback(async () => {
    if (!mode || !successfulEvmDeployment?.id) {
      throw new Error('Canonical EVM governance resource identity is unavailable')
    }
    setIsRefreshingEvmGovernance(true)
    try {
      const result = await evmOperationService.list(
        'governance_deployment',
        successfulEvmDeployment.id,
        mode,
        0,
        100
      )
      const operation = result.items.at(-1) ?? null
      setEvmGovernanceOperation(operation)
      setEvmGovernanceError(null)
      return operation
    } catch (error) {
      setEvmGovernanceError(formatEvmOperationError(error))
      throw error
    } finally {
      setIsRefreshingEvmGovernance(false)
    }
  }, [mode, successfulEvmDeployment?.id])

  const refreshEvmGovernance = useCallback(async () => {
    if (pendingEvmGovernance) {
      return reconcileEvmGovernance(pendingEvmGovernance)
    }
    return loadLatestEvmGovernance()
  }, [loadLatestEvmGovernance, pendingEvmGovernance, reconcileEvmGovernance])

  useEffect(() => {
    if (
      !isOpen
      || !isEvm
      || !successfulEvmDeployment?.id
      || pendingEvmGovernance
      || !evmGovernanceOperation
      || !EVM_TERMINAL_STATES.has(evmGovernanceOperation.state)
    ) {
      return
    }
    void loadLatestEvmGovernance().catch(() => undefined)
  }, [
    evmGovernanceOperation,
    isEvm,
    isOpen,
    loadLatestEvmGovernance,
    pendingEvmGovernance,
    successfulEvmDeployment?.id,
  ])

  useEffect(() => {
    setPendingEvmGovernance(null)
    setEvmGovernanceOperation(null)
    setEvmGovernanceError(null)
    setHasQuarantinedEvmGovernance(false)
  }, [mode, successfulEvmDeployment?.id])

  useEffect(() => {
    if (
      !isOpen
      || !isEvm
      || !mode
      || !user?.id
      || !successfulEvmDeployment?.id
    ) {
      return
    }
    try {
      const hydrated = evmOperationService.hydratePending(user.id, mode)
      const latest = hydrated.items.filter(item =>
        item.resourceType === 'governance_deployment'
        && item.resourceId === successfulEvmDeployment.id
      ).at(-1) ?? null
      setPendingEvmGovernance(latest)
      if (hydrated.quarantinedKeys.length > 0) {
        setHasQuarantinedEvmGovernance(true)
      }
      if (latest) {
        void reconcileEvmGovernance(latest).catch(() => undefined)
      } else {
        void loadLatestEvmGovernance().catch(() => undefined)
      }
    } catch (error) {
      setEvmGovernanceError(formatEvmOperationError(error))
    }
  }, [
    isEvm,
    isOpen,
    loadLatestEvmGovernance,
    mode,
    reconcileEvmGovernance,
    successfulEvmDeployment?.id,
    user?.id,
  ])

  const closeButtonColor = useColorModeValue('gray.600', 'gray.200')
  const closeButtonHover = useColorModeValue('gray.800', 'gray.50')
  const firstTabRef = useRef<HTMLButtonElement | null>(null)

  const tabs = useMemo(
    () => {
      const items = [
        { key: 'overview', label: t('orders.lifecycle.overview') },
        {
          key: 'stateConsistency',
          label: t('orders.lifecycle.stateConsistency.tabTitle'),
        },
        { key: 'documents', label: t('orders.lifecycle.documents.tabTitle', 'Docs') },
        { key: 'docPolicy', label: t('orders.lifecycle.docPolicy.tabTitle', 'Doc Policy') },
        { key: 'payments', label: t('orders.lifecycle.payments') },
        ...(!isSolana ? [{ key: 'preparation', label: t('orders.lifecycle.preparation') }] : []),
        ...(isSolana ? [{ key: 'rentRecovery', label: t('orders.lifecycle.rentRecovery.tabTitle') }] : []),
        { key: 'deployments', label: t('orders.lifecycle.deployments') },
        { key: 'settlements', label: t('orders.lifecycle.settlements') },
        { key: 'refunds', label: t('orders.lifecycle.refunds') },
        { key: 'governance', label: t('orders.lifecycle.governance.paRuClTabTitle') },
        { key: 'extendSweep', label: t('orders.lifecycle.extendSweep.tabTitle') },
        { key: 'timeline', label: t('orders.lifecycle.timeline') },
      ]
      return items
    },
    [isSolana, t]
  )
  const activeTabKey = tabs[activeTabIndex]?.key
  const isActiveTabLoading = activeTabKey === 'documents'
    ? isDocumentsLoading
    : activeTabKey === 'docPolicy'
      ? isDocPolicyLoading
      : false
  const documentOrderId = lifecycle?.order?.id ?? order?.orderId
  const documentResourceKey = `${mode}:${documentOrderId ?? 'missing'}`
  const timelineLabelMap = useMemo(
    () => (isSolana ? SOLANA_TIMELINE_LABEL_MAP : EVM_TIMELINE_LABEL_MAP),
    [isSolana]
  )

  const timeline = useMemo(() => {
    if (!lifecycle?.timeline || lifecycle.timeline.length === 0) {
      return []
    }
    return [...lifecycle.timeline]
      .filter(event => Boolean(event.timestamp))
      .sort((a, b) => a.timestamp.localeCompare(b.timestamp))
  }, [lifecycle])

  const renderAddressCell = (value?: string | null) => {
    if (!value) {
      return (
        <Text fontSize="sm" color="gray.500">
          {t('common.na')}
        </Text>
      )
    }
    return (
      <HStack spacing={1} align="center">
        <Code fontSize="xs">{truncateMiddle(value)}</Code>
        <CopyIconButton value={value} label={t('common.copy')} />
        <ExplorerButton type="address" value={value} chainId={explorerChainId} />
      </HStack>
    )
  }

  const renderTxCell = (value?: string | null) => {
    if (!value) {
      return (
        <Text fontSize="sm" color="gray.500">
          {t('common.na')}
        </Text>
      )
    }
    return (
      <HStack spacing={1} align="center">
        <Code fontSize="xs">{truncateMiddle(value)}</Code>
        <CopyIconButton value={value} label={t('common.copy')} />
        <ExplorerButton type="tx" value={value} chainId={explorerChainId} />
      </HStack>
    )
  }

  const renderAddressRow = (label: string, value?: string | null) => {
    if (!value) {
      return null
    }
    return (
      <HStack spacing={3} align="flex-start">
        <Text fontSize="sm" fontWeight="medium" minW="140px">
          {label}
        </Text>
        {renderAddressCell(value)}
      </HStack>
    )
  }

  const getOnchainOperationLabel = (operationType: string) => {
    switch (operationType) {
      case 'contract_deploy':
        return t('orders.lifecycle.onchainCosts.operation.contractDeploy')
      case 'contract_initialize':
        return t('orders.lifecycle.onchainCosts.operation.contractInitialize')
      case 'settlement_sweep':
        return t('orders.lifecycle.onchainCosts.operation.settlementSweep')
      case 'platform_meta_refund':
        return t('orders.lifecycle.onchainCosts.operation.platformMetaRefund')
      case 'governance_action':
        return t('orders.lifecycle.onchainCosts.operation.governanceAction')
      case 'sweep_extend':
        return t('orders.lifecycle.onchainCosts.operation.sweepExtend')
      default:
        return operationType.replace(/_/g, ' ')
    }
  }

  const formatUsdAmount = (raw?: string | null) => {
    if (!raw || raw.trim().length === 0) {
      return undefined
    }
    const numeric = Number.parseFloat(raw)
    if (Number.isNaN(numeric)) {
      return raw
    }
    if (Math.abs(numeric) >= 1) {
      return numeric.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    }
    if (numeric === 0) {
      return '0.00'
    }
    const formatted = numeric.toPrecision(8)
    return formatted.replace(/0+$/u, '').replace(/\.$/u, '')
  }

  const renderOnchainCostsSection = (
    title: string,
    items: OrderLifecycleOnchainCostItem[]
  ) => {
    if (!items || items.length === 0) {
      return null
    }

    const totalUsd = items.reduce((total, item) => {
      if (!item.costUsd) {
        return total
      }
      const numeric = Number.parseFloat(item.costUsd)
      return Number.isNaN(numeric) ? total : total + numeric
    }, 0)

    const formattedTotalUsd =
      Number.isFinite(totalUsd) && totalUsd > 0 ? formatUsdAmount(totalUsd.toString()) : '0.00'

    return (
      <Box borderWidth="1px" borderRadius="md" p={4}>
        <VStack align="stretch" spacing={3}>
          <Text fontSize="sm" fontWeight="semibold">
            {title}
          </Text>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3} fontSize="sm">
            <Text>
              {t('orders.lifecycle.onchainCosts.totalUsd')}: {formattedTotalUsd}
            </Text>
            <Text>
              {t('orders.lifecycle.onchainCosts.count')}: {items.length}
            </Text>
          </SimpleGrid>
          <VStack align="stretch" spacing={3} mt={2}>
            {items.map((item, index) => (
              <Box key={`${item.operationType}-${item.txHash ?? index}`} borderWidth="1px" borderRadius="md" p={3}>
                <VStack align="stretch" spacing={2}>
                  <HStack justify="space-between">
                    <Text fontWeight="medium">
                      {getOnchainOperationLabel(item.operationType)}
                    </Text>
                    <Badge colorScheme="green">{item.status}</Badge>
                  </HStack>
                  {item.txHash && (
                    <HStack spacing={3} align="flex-start">
                      <Text fontSize="sm" fontWeight="medium" minW="140px">
                        {t('orders.lifecycle.onchainCosts.txHash')}
                      </Text>
                      {renderTxCell(item.txHash)}
                    </HStack>
                  )}
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={2} fontSize="sm">
                    <Text>
                      {t('orders.lifecycle.onchainCosts.gasUsed')}: {item.gasUsed ?? t('common.na')}
                    </Text>
                    <Text>
                      {t('orders.lifecycle.onchainCosts.gasPriceWei')}: {item.gasPriceWei ?? t('common.na')}
                    </Text>
                    <Text>
                      {t('orders.lifecycle.onchainCosts.costNativeWei')}: {item.costNativeWei ?? t('common.na')}
                    </Text>
                    <Text>
                      {t('orders.lifecycle.onchainCosts.costUsd')}:{' '}
                      {formatUsdAmount(item.costUsd ?? null) ?? t('common.na')}
                    </Text>
                    <Text>
                      {t('orders.lifecycle.onchainCosts.createdAt')}:{' '}
                      {formatTimestamp(item.createdAt, locale) ?? t('common.na')}
                    </Text>
                    <Text>
                      {t('orders.lifecycle.onchainCosts.confirmedAt')}:{' '}
                      {formatTimestamp(item.confirmedAt, locale) ?? t('common.na')}
                    </Text>
                  </SimpleGrid>
                </VStack>
              </Box>
            ))}
          </VStack>
        </VStack>
      </Box>
    )
  }

const getGovernanceActionLabel = (action: GovernanceAction) => {
    switch (action) {
      case 'emergency_pause':
        return t('orders.lifecycle.governance.actions.pause')
      case 'emergency_resume':
        return t('orders.lifecycle.governance.actions.resume')
      case 'close_contract':
        return t('orders.lifecycle.governance.actions.close')
      default:
        return action
    }
  }

  const getGovernanceActionBadgeColor = (action: GovernanceAction | string) => {
    switch (action) {
      case 'emergency_pause':
        return 'orange'
      case 'emergency_resume':
        return 'green'
      case 'close_contract':
        return 'red'
      default:
        return 'blue'
    }
  }

  const getGovernanceActionKey = (action: GovernanceAction) => {
    switch (action) {
      case 'emergency_pause':
        return 'pause'
      case 'emergency_resume':
        return 'resume'
      case 'close_contract':
        return 'close'
      default:
        return action
    }
  }

  const executeGovernanceAction = useCallback(
    async (action: GovernanceAction) => {
      const orderIdentifier =
        lifecycle?.order?.id ??
        order?.orderId ??
        null

      if (!orderIdentifier) {
        showErrorToast(t('orders.lifecycle.governance.errors.missingOrderId'))
        return
      }

      let submittedEvmIdempotencyKey: string | null = null
      try {
        setActiveGovernanceAction(action)
        if (isEvm) {
          if (!user?.id || !mode || !successfulEvmDeployment?.id) {
            throw new Error('EVM governance resource identity is unavailable')
          }
          if (hasNonterminalEvmGovernance && !pendingEvmGovernance) {
            throw new Error(
              'An EVM governance operation was recovered from the server. Resolve it before starting a new action.'
            )
          }
          const notes = governanceNotes.trim() || undefined
          const expectedBody = notes ? { notes } : {}
          if (
            pendingEvmGovernance
            && (
              pendingEvmGovernance.operationType !== 'evm_governance'
              || JSON.stringify(pendingEvmGovernance.requestBody) !== JSON.stringify(expectedBody)
              || !('orderId' in pendingEvmGovernance.clientResourceLocator)
              || pendingEvmGovernance.clientResourceLocator.orderId !== orderIdentifier
              || !('action' in pendingEvmGovernance.clientResourceLocator)
              || pendingEvmGovernance.clientResourceLocator.action !== action
            )
          ) {
            throw new Error(
              'Another EVM governance operation is still pending. Resolve it before starting a new action.'
            )
          }
          const pending = pendingEvmGovernance
            ?? orderService.createEvmGovernanceOperation(
              user.id,
              orderIdentifier,
              successfulEvmDeployment.id,
              action,
              notes,
              mode
            )
          submittedEvmIdempotencyKey = pending.idempotencyKey
          setPendingEvmGovernance(pending)
          const operation = await orderService.submitEvmGovernanceOperation(
            user.id,
            pending,
            mode
          )
          setEvmGovernanceOperation(operation)
          setEvmGovernanceError(null)
          syncPendingEvmGovernance(pending.idempotencyKey)
          if (operation.state === 'rejected' || operation.state === 'failed') {
            showErrorToast({
              title: `EVM governance operation ${operation.state}`,
              description: operation.error?.message ?? operation.operationId,
            })
          } else if (operation.state === 'unknown') {
            showWarningToast({
              title: 'EVM governance operation is frozen',
              description: operation.error?.message ?? operation.operationId,
            })
          } else {
            showSuccessToast({
              title: `EVM governance operation ${operation.state}`,
              description: `Operation ID: ${operation.operationId}`,
            })
          }
          setGovernanceNotes('')
          refreshGovernanceState()
          return
        }
        const response = await orderService.triggerContractGovernance(
          orderIdentifier,
          action,
          {
            notes: governanceNotes.trim() ? governanceNotes.trim() : undefined,
          },
          mode
        )
        if (!response.success) {
          const reason = response.data?.reason ?? null
          if (reason === 'refundable_balance_nonzero') {
            const rawAmount = response.data?.refundable_balance ?? '0'
            const tokenDecimalsFromResponse =
              typeof response.data?.token_decimals === 'number'
                ? response.data.token_decimals
                : lifecycle?.order?.tokenDecimals ??
                  (order as any)?.tokenDecimals ??
                  undefined
            const normalizedAmount =
              response.data?.refundable_balance_formatted ??
              normalizeAmountFromSmallest(rawAmount, tokenDecimalsFromResponse)
            const amountText =
              formatUsdAmount(normalizedAmount ?? rawAmount) ??
              normalizedAmount ??
              rawAmount
            showWarningToast({
              title: t('orders.lifecycle.governance.warnings.refundableBalance.title'),
              description: t('orders.lifecycle.governance.warnings.refundableBalance.description', {
                amount: amountText,
              }),
              duration: 8000,
              isClosable: true,
            })
          } else {
            showErrorToast(
              t('orders.lifecycle.governance.toastError', {
                action: getGovernanceActionLabel(action),
              })
            )
          }
          refreshGovernanceState()
          return
        }
        showSuccessToast(
          t('orders.lifecycle.governance.toastSuccess', {
            action: getGovernanceActionLabel(action),
          })
        )
        setGovernanceNotes('')
        refreshGovernanceState()
      } catch (error) {
        console.error('Failed to queue governance action', error)
        if (submittedEvmIdempotencyKey) {
          try {
            syncPendingEvmGovernance(submittedEvmIdempotencyKey)
          } catch (storageError) {
            setEvmGovernanceError(formatEvmOperationError(storageError))
          }
        } else {
          setEvmGovernanceError(formatEvmOperationError(error))
        }
        showErrorToast(
          t('orders.lifecycle.governance.toastError', {
            action: getGovernanceActionLabel(action),
          })
        )
      } finally {
        setActiveGovernanceAction(null)
      }
    },
    [
      governanceNotes,
      hasNonterminalEvmGovernance,
      isEvm,
      lifecycle?.order?.id,
      mode,
      order?.orderId,
      pendingEvmGovernance,
      refreshGovernanceState,
      showErrorToast,
      showSuccessToast,
      showWarningToast,
      successfulEvmDeployment?.id,
      syncPendingEvmGovernance,
      t,
      user?.id,
    ]
  )

  const handleGovernanceAction = (action: GovernanceAction) => {
    if (
      isEvm
      && (pendingEvmGovernance !== null
        || hasNonterminalEvmGovernance
        || isRefreshingEvmGovernance)
    ) {
      setEvmGovernanceError(
        'An EVM governance operation is pending. Resolve it before starting a new action.'
      )
      return
    }
    setPendingGovernanceAction(action)
  }

  const handleManualCleanup = useCallback(async () => {
    const targetOrderId = lifecycle?.order?.id ?? order?.orderId
    if (!targetOrderId) {
      showErrorToast(t('orders.lifecycle.rentRecovery.toastMissingOrder'))
      return
    }

    try {
      setIsTriggeringManualCleanup(true)
      const response = await orderService.triggerAccountCleanup(targetOrderId, mode)
      const cleanupStatus = response.data?.status ?? 'unknown'
      const reason = response.data?.reason

      if (cleanupStatus === 'completed' || cleanupStatus === 'already_closed') {
        showSuccessToast(t('orders.lifecycle.rentRecovery.toastSuccess'))
      } else if (cleanupStatus === 'failed') {
        showErrorToast(reason || t('orders.lifecycle.rentRecovery.toastError'))
      } else {
        showWarningToast({
          title: t('orders.lifecycle.rentRecovery.toastPartialTitle'),
          description: reason || t('orders.lifecycle.rentRecovery.toastPartialDescription'),
          duration: 7000,
          isClosable: true,
        })
      }

      onRefresh?.()
    } catch (error) {
      console.error('Failed to trigger account cleanup', error)
      showErrorToast(t('orders.lifecycle.rentRecovery.toastError'))
    } finally {
      setIsTriggeringManualCleanup(false)
    }
  }, [
    lifecycle?.order?.id,
    mode,
    onRefresh,
    order?.orderId,
    showErrorToast,
    showSuccessToast,
    showWarningToast,
    t,
  ])

  const handleVerifyCleanupRecord = useCallback(async (recordId: string) => {
    const targetOrderId = lifecycle?.order?.id ?? order?.orderId
    if (!targetOrderId) {
      showErrorToast(t('orders.lifecycle.rentRecovery.toastMissingOrder'))
      return
    }

    try {
      setVerifyingCleanupRecordId(recordId)
      const response = await orderService.verifyAccountCleanup(targetOrderId, recordId, mode)
      const verificationStatus = response.data?.verificationStatus

      if (verificationStatus === 'verified') {
        showSuccessToast(t('orders.lifecycle.rentRecovery.toastVerifySuccess'))
      } else {
        showWarningToast({
          title: t('orders.lifecycle.rentRecovery.toastVerifyWarningTitle'),
          description:
            response.data?.verificationNotes ||
            t('orders.lifecycle.rentRecovery.toastVerifyWarningDescription'),
          duration: 7000,
          isClosable: true,
        })
      }

      onRefresh?.()
    } catch (error) {
      console.error('Failed to verify account cleanup', error)
      showErrorToast(t('orders.lifecycle.rentRecovery.toastVerifyError'))
    } finally {
      setVerifyingCleanupRecordId(null)
    }
  }, [
    lifecycle?.order?.id,
    mode,
    onRefresh,
    order?.orderId,
    showErrorToast,
    showSuccessToast,
    showWarningToast,
    t,
  ])

  const handleRetryCleanupRecord = useCallback(async (recordId: string) => {
    const targetOrderId = lifecycle?.order?.id ?? order?.orderId
    if (!targetOrderId) {
      showErrorToast(t('orders.lifecycle.rentRecovery.toastMissingOrder'))
      return
    }

    try {
      setRetryingCleanupRecordId(recordId)
      const response = await orderService.retryAccountCleanup(targetOrderId, recordId, mode)
      const cleanupStatus = response.data?.status ?? 'unknown'
      const reason = response.data?.reason

      if (cleanupStatus === 'completed' || cleanupStatus === 'already_closed') {
        showSuccessToast(t('orders.lifecycle.rentRecovery.toastRetrySuccess'))
      } else if (cleanupStatus === 'failed') {
        showErrorToast(reason || t('orders.lifecycle.rentRecovery.toastRetryError'))
      } else {
        showWarningToast({
          title: t('orders.lifecycle.rentRecovery.toastRetryWarningTitle'),
          description: reason || t('orders.lifecycle.rentRecovery.toastRetryWarningDescription'),
          duration: 7000,
          isClosable: true,
        })
      }

      onRefresh?.()
    } catch (error) {
      console.error('Failed to retry account cleanup', error)
      showErrorToast(t('orders.lifecycle.rentRecovery.toastRetryError'))
    } finally {
      setRetryingCleanupRecordId(null)
    }
  }, [
    lifecycle?.order?.id,
    mode,
    onRefresh,
    order?.orderId,
    showErrorToast,
    showSuccessToast,
    showWarningToast,
    t,
  ])

  const renderOverview = () => {
    const lifecycleOrder = lifecycle?.order
    if (!lifecycleOrder && !order) {
      return <Text fontSize="sm">{t('orders.lifecycle.noData')}</Text>
    }

    const tokenDecimals =
      lifecycleOrder?.tokenDecimals ??
      (order as any)?.tokenDecimals ??
      (order as any)?.token_decimals ??
      undefined
    const amountInSmallestUnit =
      lifecycleOrder?.amountInSmallestUnit ??
      (order as any)?.amount_in_smallest_unit ??
      undefined
    const normalizedAmountFromSmallest = normalizeAmountFromSmallest(
      amountInSmallestUnit,
      tokenDecimals
    )
    const primaryAmountCandidate =
      lifecycleOrder?.amountUsd ??
      order?.amountUsd ??
      normalizedAmountFromSmallest ??
      '0'
    const displayAmount =
      formatUsdAmount(primaryAmountCandidate) ??
      (normalizedAmountFromSmallest ? formatUsdAmount(normalizedAmountFromSmallest) : undefined) ??
      '0.00'

    const status = lifecycleOrder?.status ?? order?.status
    const createdAt = lifecycleOrder?.createdAt ?? order?.createdAt
    const updatedAt = lifecycleOrder?.updatedAt ?? order?.updatedAt
    const expiresAt = lifecycleOrder?.expiresAt ?? order?.expiresAt ?? undefined
    const notesToPublic = lifecycleOrder?.notesToPublic ?? order?.notesToPublic ?? undefined
    const merchantDisplay =
      lifecycleOrder?.merchant?.name ??
      lifecycleOrder?.merchant?.id ??
      order?.merchantBusinessId ??
      order?.merchantId ??
      '-'
    const chainLabel =
      lifecycleOrder?.chain?.code ??
      lifecycleOrder?.chain?.id ??
      order?.chainLabel ??
      order?.chainId ??
      '-'

    return (
      <VStack align="stretch" spacing={4}>
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
          <Box borderWidth="1px" borderRadius="md" p={4}>
            <Text fontWeight="semibold" mb={2}>
              {t('orders.lifecycle.merchant')}
            </Text>
            <VStack align="stretch" spacing={2}>
              {renderKeyValue(t('orders.table.orderId'), lifecycleOrder?.id ?? order?.orderId)}
              {renderKeyValue(t('orders.details.merchantId', 'Merchant ID'), merchantDisplay)}
              {renderKeyValue(t('orders.lifecycle.status'), formatStatus(status, t))}
              {notesToPublic ? (
                <HStack spacing={3} align="flex-start">
                  <Text fontSize="sm" fontWeight="medium" minW="140px">
                    {t('orders.lifecycle.notesToPublic')}
                  </Text>
                  <Text fontSize="sm" flex="1" whiteSpace="pre-wrap">
                    {notesToPublic}
                  </Text>
                </HStack>
              ) : null}
              {renderKeyValue(t('orders.lifecycle.amountUsd'), displayAmount)}
              {renderKeyValue(t('orders.lifecycle.createdAt'), formatTimestamp(createdAt, locale) ?? undefined)}
              {renderKeyValue(t('orders.lifecycle.updatedAt'), formatTimestamp(updatedAt, locale) ?? undefined)}
              {renderKeyValue(t('orders.lifecycle.expiresAt'), formatTimestamp(expiresAt, locale) ?? undefined)}
            </VStack>
          </Box>
          <Box borderWidth="1px" borderRadius="md" p={4}>
            <Text fontWeight="semibold" mb={2}>
              {t('orders.lifecycle.chain')}
            </Text>
            <VStack align="stretch" spacing={2}>
              {renderKeyValue(t('orders.lifecycle.chain'), chainLabel)}
              {isSolana && renderAddressRow(
                t('orders.lifecycle.paymentAddress', 'Payment Address'),
                lifecycleOrder?.paymentAddress ?? order?.paymentAddress
              )}
              {isSolana && renderAddressRow(
                t('orders.lifecycle.vaultTokenAccount', 'Vault Token Account'),
                lifecycleOrder?.vaultTokenAccount
              )}
              {renderAddressRow(
                isSolana
                  ? t('orders.lifecycle.tokenMint', 'Token Mint')
                  : t('orders.lifecycle.tokenAddress', 'Token Address'),
                lifecycleOrder?.paymentTokenAddress
              )}
              {renderAddressRow(
                t('orders.lifecycle.programAddress', 'Program Address'),
                lifecycleOrder?.programId
              )}
              {renderAddressRow(
                t('orders.lifecycle.orderStateAddress', 'Order State Address'),
                lifecycleOrder?.orderStatePda
              )}
            {/* Amount not repeated in chain card */}
            </VStack>
          </Box>
        </SimpleGrid>
      </VStack>
    )
  }

  const renderPreparation = () => {
    const registry = lifecycle?.preparation?.registry
    if (!registry) {
      return <Text fontSize="sm">{t('orders.lifecycle.noData')}</Text>
    }

    return (
      <VStack align="stretch" spacing={4}>
        <Box borderWidth="1px" borderRadius="md" p={4}>
          <VStack align="stretch" spacing={3}>
            {renderKeyValue(t('orders.lifecycle.status'), formatStatus(registry.status, t))}
            <HStack spacing={3} align="flex-start">
              <Text fontSize="sm" fontWeight="medium" minW="140px">
                {t('orders.lifecycle.merchantAddress', 'Merchant Address')}
              </Text>
              {renderAddressCell(registry.merchantAddress)}
            </HStack>
            <HStack spacing={3} align="flex-start">
              <Text fontSize="sm" fontWeight="medium" minW="140px">
                {t('orders.lifecycle.contract', 'Contract Address')}
              </Text>
              {renderAddressCell(registry.predictedContractAddress)}
            </HStack>
            {renderAddressRow(
              t('orders.lifecycle.tokenAddress', 'Token Address'),
              registry.tokenAddress
            )}
            {renderAddressRow(
              t('orders.lifecycle.programAddress', 'Program Address'),
              registry.programId
            )}
            {renderAddressRow(
              t('orders.lifecycle.orderStateAddress', 'Order State Address'),
              registry.orderStatePda
            )}
            {renderAddressRow(
              t('orders.lifecycle.orderVaultAuthorityAddress', 'Order Vault Authority'),
              registry.orderVaultAuthorityPda
            )}
            {registry.platformAddresses && (
              <Box>
                <Text fontSize="sm" fontWeight="medium" mb={1}>
                  {t('orders.lifecycle.platformAddresses')}
                </Text>
                <VStack spacing={1} fontSize="sm" align="stretch">
                  {Object.entries(registry.platformAddresses).map(([key, value]) => (
                    <HStack key={key} spacing={2} align="flex-start">
                      <Text fontWeight="medium" minW="80px">
                        {key}:
                      </Text>
                      {renderAddressCell(typeof value === 'string' ? value : String(value))}
                    </HStack>
                  ))}
                </VStack>
              </Box>
            )}
            {Array.isArray(registry.userPayments) && registry.userPayments.length > 0 && (
              <Box>
                <Text fontSize="sm" fontWeight="medium" mb={1}>
                  {t('orders.lifecycle.userPayments')}
                </Text>
                <VStack align="stretch" spacing={2}>
                  {registry.userPayments.map((payment, index) => (
                    <HStack key={`${payment.address ?? 'payment'}-${index}`} spacing={3} fontSize="sm">
                      {renderAddressCell(payment.address)}
                      <Badge>{formatAmount(payment.amountUsd)}</Badge>
                    </HStack>
                  ))}
                </VStack>
              </Box>
            )}
            {renderKeyValue(
              t('orders.lifecycle.createdAt'),
              formatTimestamp(registry.createdAt, locale) ?? undefined
            )}
            {renderKeyValue(
              t('orders.lifecycle.updatedAt'),
              formatTimestamp(registry.updatedAt, locale) ?? undefined
            )}
          </VStack>
        </Box>
      </VStack>
    )
  }

  const renderRentRecovery = () => {
    return (
      <OrderRentRecoverySection
        records={lifecycle?.rentRecovery?.records as OrderLifecycleRentRecoveryRecord[] | undefined}
        locale={locale}
        t={t}
        formatStatus={formatStatus}
        formatTimestamp={formatTimestamp}
        renderTxCell={renderTxCell}
        onTriggerManualCleanup={isSolana ? handleManualCleanup : undefined}
        isTriggeringManualCleanup={isTriggeringManualCleanup}
        onVerifyRecord={isSolana ? handleVerifyCleanupRecord : undefined}
        verifyingRecordId={verifyingCleanupRecordId}
        onRetryRecord={isSolana ? handleRetryCleanupRecord : undefined}
        retryingRecordId={retryingCleanupRecordId}
      />
    )
  }

  const renderSweepExtendSummary = (extend: OrderLifecycleSweepExtend | null | undefined) => {
    if (!extend) {
      return null
    }

    const notes = Array.isArray(extend.sweepExtendNotes) ? extend.sweepExtendNotes : []
    const rawTotalExtensions =
      typeof extend.totalExtensions === 'number' ? extend.totalExtensions : 0
    const totalExtensions = Math.max(rawTotalExtensions, notes.length)

    const formatOrNA = (value?: string | null) => formatTimestamp(value, locale) ?? (value ?? t('common.na'))

    const hasActivity = notes.length > 0 || (totalExtensions ?? 0) > 0

    return (
      <Box borderWidth="1px" borderRadius="md" p={4}>
        <VStack align="stretch" spacing={2}>
          <Text fontSize="sm" fontWeight="medium">
            {t('orders.lifecycle.sweepExtendSummary', 'Sweep extension summary')}
          </Text>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3} fontSize="sm">
            <Text>
              {t('orders.lifecycle.sweepUnlock')}: {formatOrNA(extend.currentUnlockAt)}
            </Text>
            <Text>
              {t('orders.lifecycle.sweepUnlockMax')}: {formatOrNA(extend.sweepExtendMaxAt)}
            </Text>
            <Text>
              {t('orders.lifecycle.sweepExtendCount', 'Total extensions')}: {totalExtensions ?? 0}
            </Text>
          </SimpleGrid>
          {hasActivity ? (
            <VStack align="stretch" spacing={3} mt={2}>
              {extend.sweepExtendNotes?.[0]?.note && (
                <Text fontSize="sm" color="gray.600">
                  {extend.sweepExtendNotes[0].note}
                </Text>
              )}
              {notes.map((note, index) => {
                const requestedAt = formatOrNA(note.requestedAt)
                const requestedUnlock = formatOrNA(note.requestedUnlockAt)
                const resultingUnlock = note.resultingUnlockAt ? formatOrNA(note.resultingUnlockAt) : undefined
                const executedAt = note.executedAt ? formatOrNA(note.executedAt) : undefined
                const extendDuration =
                  typeof note.extendSeconds === 'number'
                    ? `${Math.max(0, Math.floor(note.extendSeconds / 3600))}h`
                    : t('common.na')

                return (
                  <Box key={`${note.requestedAt ?? 'note'}-${index}`} borderWidth="1px" borderRadius="md" p={3}>
                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={2} fontSize="sm">
                      <Text>
                        {t('orders.lifecycle.requestedAt')}: {requestedAt}
                      </Text>
                      <Text>
                        {t('orders.lifecycle.requestedUnlockAt')}: {requestedUnlock}
                      </Text>
                      <Text>
                        {t('orders.lifecycle.resultingUnlockAt')}: {resultingUnlock ?? t('common.na')}
                      </Text>
                      <Text>
                        {t('orders.lifecycle.executedAt')}: {executedAt ?? t('common.na')}
                      </Text>
                      <Text>
                        {t('orders.lifecycle.extendDuration')}: {extendDuration}
                      </Text>
                    </SimpleGrid>
                    {note.note && (
                      <Text fontSize="sm" mt={2}>
                        {note.note}
                      </Text>
                    )}
                  </Box>
                )
              })}
            </VStack>
          ) : (
            <Text fontSize="sm" color="gray.500">
              {t('orders.lifecycle.noExtensionActivity', 'No extension activity recorded.')}
            </Text>
          )}
        </VStack>
      </Box>
    )
  }

  const renderExtendSweepTab = () => {
    const deployments = lifecycle?.deployments ?? []
    const sweepExtend = lifecycle?.sweepExtend ?? null
    const verifiedDeployment = deployments.find(
      deployment =>
        (deployment.status ?? '').toLowerCase() === 'successful' &&
        (!deployment.verification?.status || (deployment.verification.status ?? '').toLowerCase() === 'verified')
    )
    const extendInfo: OrderLifecycleSweepExtend | null = sweepExtend ?? null
    const verifiedDeploymentUnlock = verifiedDeployment?.verification?.sweepOpenAt ?? null
    const currentUnlockSource = extendInfo?.currentUnlockAt ?? verifiedDeploymentUnlock ?? null
    const maxUnlockSource = extendInfo?.sweepExtendMaxAt ?? null
    const now = new Date()
    const currentUnlockDate =
      currentUnlockSource && !Number.isNaN(Date.parse(currentUnlockSource)) ? new Date(currentUnlockSource) : null
    const maxUnlockDate = maxUnlockSource && !Number.isNaN(Date.parse(maxUnlockSource)) ? new Date(maxUnlockSource) : null
    const fallbackExtendInfo: OrderLifecycleSweepExtend | null =
      !extendInfo && currentUnlockSource
        ? {
            currentUnlockAt: currentUnlockSource,
            sweepExtendMaxAt: maxUnlockSource ?? (maxUnlockDate ? maxUnlockDate.toISOString() : null),
            totalExtensions: null,
            sweepExtendNotes: [],
          }
        : null
    const summaryInfo = extendInfo ?? fallbackExtendInfo
    const fallbackContractVerified = Boolean(verifiedDeployment)
    const contractDeployVerified = summaryInfo?.contractDeployVerified ?? fallbackContractVerified
    const fallbackUnlockStillOpen =
      currentUnlockDate && !Number.isNaN(currentUnlockDate.getTime()) ? now.getTime() < currentUnlockDate.getTime() : false
    const unlockStillOpen = summaryInfo?.currentUnlockIsFuture ?? fallbackUnlockStillOpen
    const settlementTriggered = summaryInfo?.settlementTriggered ?? false
    const normalizedOrderStatus = (lifecycle?.order?.status ?? order?.status ?? '').toLowerCase()
    const fallbackOrderFullyRefunded = normalizedOrderStatus === 'refunded' || normalizedOrderStatus === 'full_refunded'
    const isOrderFullyRefunded = summaryInfo?.isOrderFullyRefunded ?? fallbackOrderFullyRefunded
    const isEligible = contractDeployVerified && unlockStillOpen && !settlementTriggered && !isOrderFullyRefunded
    const orderIdForExtend = order?.orderId ?? lifecycle?.order?.id ?? ''
    const selectedDate = newUnlockAt ? new Date(newUnlockAt) : null
    const selectedTimestamp =
      selectedDate && !Number.isNaN(selectedDate.getTime()) ? selectedDate.getTime() : undefined

    let validationMessage: string | null = null
    if (newUnlockAt) {
      if (!selectedTimestamp) {
        validationMessage = t('orders.lifecycle.extendSweep.validation.invalidFormat')
      } else if (selectedTimestamp <= now.getTime()) {
        validationMessage = t('orders.lifecycle.extendSweep.validation.futureRequired')
      } else if (currentUnlockDate && selectedTimestamp <= currentUnlockDate.getTime()) {
        validationMessage = t('orders.lifecycle.extendSweep.validation.afterCurrent')
      } else if (maxUnlockDate && selectedTimestamp > maxUnlockDate.getTime()) {
        validationMessage = t('orders.lifecycle.extendSweep.validation.beforeMax')
      }
    }

    const canSubmit = Boolean(orderIdForExtend) && isEligible && Boolean(selectedTimestamp) && !validationMessage && !isExtending

    const minInputDate = currentUnlockDate && !Number.isNaN(currentUnlockDate.getTime()) && unlockStillOpen ? currentUnlockDate : now
    const minInputValue = toLocalInputValue(minInputDate)
    const maxInputValue = toLocalInputValue(maxUnlockDate)

    const checklistItems = [
      {
        key: 'contractDeployVerified',
        passed: contractDeployVerified,
        label: t('orders.lifecycle.extendSweep.checklist.contractDeployVerified'),
        description: t('orders.lifecycle.extendSweep.checklist.contractDeployVerifiedDescription'),
      },
      {
        key: 'unlockInFuture',
        passed: unlockStillOpen,
        label: t('orders.lifecycle.extendSweep.checklist.beforeUnlock'),
        description: t('orders.lifecycle.extendSweep.checklist.beforeUnlockDescription'),
      },
      {
        key: 'settlementNotTriggered',
        passed: !settlementTriggered,
        label: t('orders.lifecycle.extendSweep.checklist.settlementNotTriggered'),
        description: t('orders.lifecycle.extendSweep.checklist.settlementNotTriggeredDescription'),
      },
      {
        key: 'orderNotFullyRefunded',
        passed: !isOrderFullyRefunded,
        label: t('orders.lifecycle.extendSweep.checklist.orderNotFullyRefunded'),
        description: t('orders.lifecycle.extendSweep.checklist.orderNotFullyRefundedDescription'),
      },
    ]

    const summarySection = renderSweepExtendSummary(summaryInfo)

    if (!summaryInfo) {
      return (
        <Alert status="info" borderRadius="md">
          <AlertIcon />
          <Text fontSize="sm">{t('orders.lifecycle.extendSweep.noData')}</Text>
        </Alert>
      )
    }

    const formattedCurrentUnlock =
      summaryInfo.currentUnlockAt
        ? formatTimestamp(summaryInfo.currentUnlockAt, locale) ?? summaryInfo.currentUnlockAt
        : t('common.na')

    const handleExtend = async () => {
      if (!canSubmit || !selectedTimestamp || !orderIdForExtend) {
        return
      }
      try {
        setIsExtending(true)
        const payload = {
          updatedUnlockAt: new Date(selectedTimestamp).toISOString(),
          ...(extendNotes.trim().length > 0 ? { sweepExtendNotes: extendNotes.trim() } : {}),
        }
        const response = await settlementService.extendSweepUnlock(orderIdForExtend, payload)
        showSuccessToast({
          title: t('orders.lifecycle.extendSweep.toastSuccessTitle'),
          description: response.requestedUnlockAt
            ? t('orders.lifecycle.extendSweep.toastSuccessDescriptionWithTime', {
                time: formatTimestamp(response.requestedUnlockAt, locale) ?? response.requestedUnlockAt,
              })
            : t('orders.lifecycle.extendSweep.toastSuccessDescription'),
        })
        setNewUnlockAt('')
        setExtendNotes('')
        if (onRefresh) {
          onRefresh()
        }
      } catch (error) {
        const message =
          (error as any)?.response?.data?.error ??
          (error instanceof Error ? error.message : t('orders.lifecycle.extendSweep.toastErrorFallback'))
        showErrorToast({
          title: t('orders.lifecycle.extendSweep.toastErrorTitle'),
          description: message,
        })
      } finally {
        setIsExtending(false)
      }
    }

    const extendCosts = onchainCosts.filter(item => item.operationType === 'sweep_extend')

    return (
      <VStack align="stretch" spacing={4}>
        {summarySection}

        <Box borderWidth="1px" borderRadius="md" p={4}>
          <Text fontWeight="semibold" mb={2}>
            {t('orders.lifecycle.extendSweep.checklistTitle')}
          </Text>
          <List spacing={3}>
            {checklistItems.map(item => (
              <ListItem key={item.key}>
                <HStack align="flex-start" spacing={3}>
                  <ListIcon as={item.passed ? CheckCircleIcon : WarningIcon} color={item.passed ? 'green.500' : 'orange.400'} mt={0.5} />
                  <Box>
                    <Text fontWeight="medium">{item.label}</Text>
                    <Text fontSize="sm" color="gray.500">
                      {item.description}
                    </Text>
                  </Box>
                </HStack>
              </ListItem>
            ))}
          </List>
          {!isEligible && (
            <Alert status="warning" mt={4} borderRadius="md">
              <AlertIcon />
              <Text fontSize="sm">{t('orders.lifecycle.extendSweep.ineligibleMessage')}</Text>
            </Alert>
          )}
        </Box>

        <Box borderWidth="1px" borderRadius="md" p={4}>
          <VStack align="stretch" spacing={4}>
            <Box>
              <Text fontWeight="semibold">{t('orders.lifecycle.extendSweep.durationTitle')}</Text>
              <Text fontSize="sm" color="gray.500">
                {t('orders.lifecycle.extendSweep.durationDescription')}
              </Text>
            </Box>
            <SimpleGrid columns={{ base: 2, sm: 4 }} spacing={4}>
              <FormControl>
                <FormLabel fontSize="sm">{t('orders.lifecycle.extendSweep.duration.years')}</FormLabel>
                <NumberInput min={0} value={durationOffsets.years} onChange={(_, value) => handleDurationChange('years', value)} size="sm">
                  <NumberInputField />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
              </FormControl>
              <FormControl>
                <FormLabel fontSize="sm">{t('orders.lifecycle.extendSweep.duration.months')}</FormLabel>
                <NumberInput min={0} value={durationOffsets.months} onChange={(_, value) => handleDurationChange('months', value)} size="sm">
                  <NumberInputField />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
              </FormControl>
              <FormControl>
                <FormLabel fontSize="sm">{t('orders.lifecycle.extendSweep.duration.days')}</FormLabel>
                <NumberInput min={0} value={durationOffsets.days} onChange={(_, value) => handleDurationChange('days', value)} size="sm">
                  <NumberInputField />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
              </FormControl>
              <FormControl>
                <FormLabel fontSize="sm">{t('orders.lifecycle.extendSweep.duration.hours')}</FormLabel>
                <NumberInput min={0} value={durationOffsets.hours} onChange={(_, value) => handleDurationChange('hours', value)} size="sm">
                  <NumberInputField />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
              </FormControl>
            </SimpleGrid>
            <Box>
              <Button size="sm" variant="outline" onClick={() => applyDurationOffsets(currentUnlockDate ?? now)}>
                {t('orders.lifecycle.extendSweep.applyDuration')}
              </Button>
              <Text fontSize="xs" color="gray.500" mt={2}>
                {t('orders.lifecycle.extendSweep.durationHint')}
              </Text>
            </Box>
          </VStack>
        </Box>

        <Box borderWidth="1px" borderRadius="md" p={4}>
          <VStack align="stretch" spacing={4}>
            <FormControl isRequired isInvalid={Boolean(validationMessage)}>
              <FormLabel>{t('orders.lifecycle.extendSweep.newUnlockLabel')}</FormLabel>
              <Input
                type="datetime-local"
                value={newUnlockAt}
                onChange={event => setNewUnlockAt(event.target.value)}
                min={minInputValue || undefined}
                max={maxInputValue || undefined}
              />
              {validationMessage ? (
                <FormErrorMessage fontSize="sm">{validationMessage}</FormErrorMessage>
              ) : (
                <FormHelperText fontSize="sm">
                  {t('orders.lifecycle.extendSweep.newUnlockHelp', {
                    current: formattedCurrentUnlock,
                  })}
                </FormHelperText>
              )}
            </FormControl>
            <FormControl>
              <FormLabel>{t('orders.lifecycle.extendSweep.notesLabel')}</FormLabel>
              <Textarea
                value={extendNotes}
                onChange={event => setExtendNotes(event.target.value)}
                placeholder={t('orders.lifecycle.extendSweep.notesPlaceholder')}
                resize="vertical"
                rows={3}
              />
              <FormHelperText fontSize="sm">{t('orders.lifecycle.extendSweep.notesOptional')}</FormHelperText>
            </FormControl>
            <Button colorScheme="admin" alignSelf="flex-start" onClick={handleExtend} isDisabled={!canSubmit} isLoading={isExtending}>
              {t('orders.lifecycle.extendSweep.submit')}
            </Button>
          </VStack>
        </Box>
        {renderOnchainCostsSection(t('orders.lifecycle.onchainCosts.title'), extendCosts)}
      </VStack>
    )
  }

  const renderContent = () => {
    if (isLoading && !lifecycle) {
      return (
        <Center py={16}>
          <Spinner size="lg" label={t('orders.lifecycle.loading')} />
        </Center>
      )
    }

    if (!lifecycle || tabs.length === 0) {
      return (
        <Alert status="info" borderRadius="md">
          <AlertIcon />
          {t('orders.lifecycle.noData')}
        </Alert>
      )
    }

    return (
      <Box>
        {onRefresh && (
          <Box textAlign="right" mb={2}>
            <RefreshIconButton
              label={t('common.refresh')}
              onClick={handleUnifiedRefresh}
              isLoading={Boolean(isLoading) || isActiveTabLoading}
            />
          </Box>
        )}
        <Tabs
          colorScheme="blue"
          variant="enclosed"
          index={activeTabIndex}
          onChange={setActiveTabIndex}
        >
          <TabList
            overflowX={{ base: 'auto', md: 'visible' }}
            overflowY="hidden"
          >
            {tabs.map((tab, index) => (
              <Tab
                key={tab.key}
                ref={index === 0 ? firstTabRef : undefined}
                fontSize="xs"
                fontWeight="medium"
                px={2}
                py={2}
                minH="32px"
                whiteSpace="nowrap"
              >
                {tab.label}
              </Tab>
            ))}
          </TabList>
          <TabPanels>
            {tabs.map(tab => (
              <TabPanel key={tab.key} px={0} py={4}>
                {tab.key === 'overview' && renderOverview()}
                {tab.key === 'stateConsistency' && (
                  <OrderStateConsistencyTab
                    orderId={lifecycle?.order?.id ?? order?.orderId ?? null}
                    isActive={tabs[activeTabIndex]?.key === 'stateConsistency'}
                  />
                )}
                {tab.key === 'documents' && (
                  <OrderLifecycleDocumentsTab
                    key={`${documentResourceKey}:documents`}
                    orderId={documentOrderId}
                    isActive={activeTabKey === 'documents'}
                    mode={mode}
                    onRefresh={onRefresh}
                    refreshToken={documentTabsRefreshToken}
                    onLoadingChange={setIsDocumentsLoading}
                  />
                )}
                {tab.key === 'docPolicy' && (
                  <OrderLifecycleDocPolicyTab
                    key={`${documentResourceKey}:doc-policy`}
                    orderId={documentOrderId}
                    isActive={activeTabKey === 'docPolicy'}
                    mode={mode}
                    refreshToken={documentTabsRefreshToken}
                    onLoadingChange={setIsDocPolicyLoading}
                  />
                )}
                {tab.key === 'payments' && (
                  <OrderPaymentsSection
                    payments={lifecycle.payments}
                    paymentAddress={lifecycle.order?.paymentAddress ?? order?.paymentAddress}
                    explorerChainId={explorerChainId}
                    displayChainLabel={displayChainLabel}
                    chainCompatibility={chainCompatibility}
                    t={t}
                    formatAmount={formatAmount}
                    formatTimestamp={formatTimestamp}
                    truncateMiddle={truncateMiddle}
                    locale={locale}
                  />
                )}
                {tab.key === 'preparation' && renderPreparation()}
                {tab.key === 'rentRecovery' && renderRentRecovery()}
                {tab.key === 'deployments' && (
                  <VStack align="stretch" spacing={4}>
                    <OrderDeploymentsSection
                      deployments={lifecycle.deployments}
                      sweepExtend={lifecycle.sweepExtend}
                      paymentAddress={lifecycle.order?.paymentAddress ?? order?.paymentAddress}
                      renderTxCell={renderTxCell}
                      renderAddressCell={renderAddressCell}
                      formatStatus={formatStatus}
                      formatTimestamp={formatTimestamp}
                      renderKeyValue={renderKeyValue}
                      t={t}
                      locale={locale}
                      chainId={explorerChainId}
                      chainCompatibility={chainCompatibility}
                    />
                    {renderOnchainCostsSection(
                      t('orders.lifecycle.onchainCosts.title'),
                      onchainCosts.filter(item => {
                        return ['contract_deploy', 'contract_initialize'].includes(item.operationType)
                      })
                    )}
                  </VStack>
                )}
                {tab.key === 'settlements' && (
                  <VStack align="stretch" spacing={4}>
                    <OrderSettlementsSection
                      settlements={lifecycle.settlements}
                      lifecycleStatus={lifecycle?.order?.status ?? order?.status ?? null}
                      locale={locale}
                      t={t}
                      formatStatus={formatStatus}
                      formatAmount={formatAmount}
                      formatTimestamp={formatTimestamp}
                      renderTxCell={renderTxCell}
                      chainId={explorerChainId}
                    />
                    {renderOnchainCostsSection(
                      t('orders.lifecycle.onchainCosts.title'),
                      onchainCosts.filter(item => item.operationType === 'settlement_sweep')
                    )}
                  </VStack>
                )}
                {tab.key === 'refunds' && (
                  <VStack align="stretch" spacing={4}>
                    <OrderRefundsSection
                      refunds={lifecycle.refunds}
                      locale={locale}
                      t={t}
                      formatAmount={formatAmount}
                      formatTimestamp={formatTimestamp}
                      truncateMiddle={truncateMiddle}
                      formatStatus={formatStatus}
                    />
                    {renderOnchainCostsSection(
                      t('orders.lifecycle.onchainCosts.title'),
                      onchainCosts.filter(item => item.operationType === 'platform_meta_refund')
                    )}
                  </VStack>
                )}
                {tab.key === 'governance' && (
                  <VStack align="stretch" spacing={4}>
                    {isEvm && (
                      pendingEvmGovernance
                      || evmGovernanceOperation
                      || evmGovernanceError
                      || hasQuarantinedEvmGovernance
                    ) && (
                      <Alert
                        status={
                          hasQuarantinedEvmGovernance
                            ? 'warning'
                            : evmGovernanceOperation?.state === 'failed'
                          || evmGovernanceOperation?.state === 'rejected'
                            ? 'error'
                            : evmGovernanceOperation?.state === 'unknown'
                              || evmGovernanceError
                              ? 'warning'
                              : 'info'
                        }
                        borderRadius="md"
                      >
                        <AlertIcon />
                        <Box flex="1">
                          <Text fontWeight="semibold">
                            EVM execution operation:{' '}
                            {evmGovernanceOperation?.state ?? 'pending'}
                          </Text>
                          <Text fontSize="sm">
                            {hasQuarantinedEvmGovernance
                              ? 'A stored EVM operation is invalid and has been isolated. It was not submitted.'
                              : evmGovernanceError
                              ?? (evmGovernanceOperation
                                ? `Operation ${evmGovernanceOperation.operationId}. This execution state is separate from order state.`
                                : 'The original request identity is persisted and will be reused.')}
                          </Text>
                        </Box>
                        {(
                          pendingEvmGovernance
                          || hasNonterminalEvmGovernance
                          || evmGovernanceError
                        ) && (
                          <Button
                            size="sm"
                            ml={3}
                            variant="outline"
                            isLoading={isRefreshingEvmGovernance}
                            onClick={() => {
                              void refreshEvmGovernance().catch(() => undefined)
                            }}
                          >
                            Refresh operation
                          </Button>
                        )}
                      </Alert>
                    )}
                    <OrderGovernanceSection
                      events={lifecycle.contractPauseResumeCloseEvents}
                      lifecycle={lifecycle}
                      isSolana={isSolana}
                      solanaOrderPauseSupported={order?.solanaOrderPauseSupported}
                      solanaGovernanceState={solanaGovernanceStateQuery.data}
                      isSolanaGovernanceStateLoading={solanaGovernanceStateQuery.isFetching}
                      isSolanaGovernanceStateError={solanaGovernanceStateQuery.isError}
                      governanceNotes={governanceNotes}
                      onGovernanceNotesChange={setGovernanceNotes}
                      activeGovernanceAction={activeGovernanceAction}
                      isLoading={Boolean(isLoading)}
                      onRequestAction={handleGovernanceAction}
                      getGovernanceActionLabel={getGovernanceActionLabel}
                      getGovernanceActionBadgeColor={getGovernanceActionBadgeColor}
                      formatTimestamp={formatTimestamp}
                      locale={locale}
                      t={t}
                    />
                    {renderOnchainCostsSection(
                      t('orders.lifecycle.onchainCosts.title'),
                      onchainCosts.filter(item => item.operationType === 'governance_action')
                    )}
                  </VStack>
                )}
                {tab.key === 'extendSweep' && renderExtendSweepTab()}
                {tab.key === 'timeline' && (
                    <OrderLifecycleTimeline
                      events={timeline}
                      timelineLabelMap={timelineLabelMap}
                      formatTimestamp={formatTimestamp}
                      chainCompatibility={chainCompatibility}
                      locale={locale}
                      t={t}
                    />
                )}
              </TabPanel>
            ))}
          </TabPanels>
        </Tabs>
      </Box>
    )
  }

  const handleClose = () => {
    onClose()
  }

  const renderGovernanceConfirmDialog = () => {
    if (!pendingGovernanceAction) {
      return null
    }

    const actionKey = getGovernanceActionKey(pendingGovernanceAction)
    const confirmMessage = t(`orders.lifecycle.governance.confirm.${actionKey}`)

    return (
      <AlertDialog
        isOpen={pendingGovernanceAction !== null}
        leastDestructiveRef={cancelGovernanceRef}
        onClose={() => setPendingGovernanceAction(null)}
        isCentered
        size="md"
      >
        <AlertDialogOverlay />
        <AlertDialogContent>
          <AlertDialogHeader fontSize="lg" fontWeight="bold">
            {t('orders.lifecycle.governance.confirm.title', { action: getGovernanceActionLabel(pendingGovernanceAction) })}
          </AlertDialogHeader>
          <AlertDialogBody>{confirmMessage}</AlertDialogBody>
          <AlertDialogFooter>
            <Button ref={cancelGovernanceRef} onClick={() => setPendingGovernanceAction(null)}>
              {t('common.cancel')}
            </Button>
            <Button
              colorScheme="blue"
              ml={3}
              isLoading={activeGovernanceAction === pendingGovernanceAction}
              onClick={async () => {
                const action = pendingGovernanceAction
                setPendingGovernanceAction(null)
                await executeGovernanceAction(action)
              }}
            >
              {t('common.confirm')}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    )
  }

  return (
    <>
      <Modal
      isOpen={isOpen}
      onClose={handleClose}
      size="6xl"
      scrollBehavior="inside"
      initialFocusRef={firstTabRef}
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          {t('orders.lifecycle.title')}
          {order?.orderId && (
            <Text fontSize="sm" color="gray.500" mt={1}>
              {order.orderId}
            </Text>
          )}
        </ModalHeader>
        <ModalCloseButton color={closeButtonColor} _hover={{ color: closeButtonHover }} _focus={{ boxShadow: 'none' }} />
        <ModalBody pb={6}>{renderContent()}</ModalBody>
      </ModalContent>
      </Modal>
      {renderGovernanceConfirmDialog()}
    </>
  )
}
